/* Shared helpers for Export Certificate usage accounting.

   The cert's "Used" count is DERIVED live from gfi-shipments — never
   read from a stored counter on the cert record. One UNIQUE shipper
   invoice that references the cert (via any line.exportCertId or
   line.exportCertNumber match) counts as ONE use, regardless of how
   many product lines on that shipment reference the cert. When
   shipperInvoice is empty we fall back to shipmentId as the dedup key
   so unrelated empty-invoice shipments don't collapse together.

   "Commodity" = the produce type (Blackberry / Blueberry / Raspberry /
   etc.) ignoring pack size, label, and category. A cert covers ONE
   commodity, but the lines using it on a shipment can be any
   combination of sizes / labels of that commodity.
*/

export function nrmCertNum(s) { return String(s || "").toLowerCase().replace(/\s+/g, "").trim(); }

/* Known commodity tokens. Extend if the business adds more produce
   types. Match is case-insensitive substring on the productName field
   of either a cert record or a shipment line. */
export var COMMODITIES = ["Blackberry", "Blueberry", "Raspberry", "Strawberry", "Avocado", "Lime"];

export function extractCommodity(productStr) {
  if (!productStr) return "";
  var s = String(productStr).toLowerCase();
  for (var i = 0; i < COMMODITIES.length; i++) {
    if (s.indexOf(COMMODITIES[i].toLowerCase()) >= 0) return COMMODITIES[i];
  }
  return "";
}

/* Returns one usage entry per UNIQUE shipper invoice that references
   the cert. Each entry: { shipmentId, lot, lots, shipperInvoice, date }.
   When the SAME shipper invoice covers multiple shipments (e.g. one
   invoice GB-A-4884 covers lots 57916 + 57917), all lots are collected
   into `lots: ["57916", "57917"]` and surfaced as a comma-joined string
   on `lot` for backwards-compatible display in the on-screen
   AssignedInvoices table and the audit report. The dedup count stays
   at 1 — same invoice = same slot.

   `bills` is optional: when supplied, gfi-bills records that reference
   the cert via their lines (exportCertId / exportCertNumber /
   exportCertManual) count as a usage too. Same dedup key — a bill and
   shipment sharing a shipperInvoice collapse into one slot. This
   matters when the user imported Bills without also importing the
   corresponding Shipments (the cert link still exists on the bill
   lines and must consume a cert slot).

   `portalEmbarques` is optional: gfi-shipper-shipments records (the
   shipper portal's own embarques) live in a separate store from the
   GFI-side gfi-shipments. The portal's nested shape is
   embarque.proformas[].lines[]; we walk that and dedup against the
   embarque's shipperInvoice (or embarque id as fallback) so an
   embarque whose accepted proforma is also already in `shipments`
   counts once, not twice. */
export function computeCertUsage(cert, shipments, bills, portalEmbarques) {
  if (!cert) return [];
  var idKey = String(cert.id || "");
  var numKey = nrmCertNum(cert.certNumber);
  var out = []; var keyToIdx = {};
  function appendLot(entry, lot) {
    if (lot == null || lot === "") return;
    var s = String(lot);
    if (!entry.lots) entry.lots = [];
    if (entry.lots.indexOf(s) >= 0) return;
    entry.lots.push(s);
    entry.lot = entry.lots.join(", ");
  }
  function upsert(key, seed, lot) {
    if (keyToIdx[key] !== undefined) {
      appendLot(out[keyToIdx[key]], lot);
      return;
    }
    var entry = {
      shipmentId: seed.shipmentId || "",
      lot: lot ? String(lot) : "",
      lots: lot ? [String(lot)] : [],
      shipperInvoice: seed.shipperInvoice || "",
      date: seed.date || ""
    };
    keyToIdx[key] = out.length;
    out.push(entry);
  }
  function lineRefsCert(l) {
    if (idKey && String(l.exportCertId || "") === idKey) return true;
    if (numKey && nrmCertNum(l.exportCertNumber) === numKey) return true;
    if (numKey && nrmCertNum(l.exportCertManual) === numKey) return true;
    return false;
  }
  (shipments || []).forEach(function (s) {
    var lines = s.lines || [];
    var hit = lines.some(lineRefsCert);
    if (!hit) return;
    var key = s.shipperInvoice ? ("inv:" + s.shipperInvoice) : ("shp:" + s.id);
    upsert(key, {
      shipmentId: s.id,
      shipperInvoice: s.shipperInvoice || "",
      date: s.dateArrival || s.dateDeparture || ""
    }, s.lot);
  });
  /* Bills: scan lines for cert references. Same dedup key as shipments
     (shipperInvoice) so a bill whose corresponding shipment is also
     in `shipments` doesn't double-count. shipmentId comes from
     bill.shipmentId if set, else the bill's own id as fallback. */
  (bills || []).forEach(function (b) {
    var lines = b.lines || [];
    var hit = lines.some(lineRefsCert);
    if (!hit) return;
    var key = b.shipperInvoice ? ("inv:" + b.shipperInvoice) : ("bill:" + b.id);
    upsert(key, {
      shipmentId: b.shipmentId || b.id,
      shipperInvoice: b.shipperInvoice || "",
      date: b.billDate || ""
    }, b.lot);
  });
  /* Portal embarques: nested embarque.proformas[].lines[]. Each
     embarque's shipperInvoice is the dedup key so an accepted proforma
     that was already promoted into `shipments` doesn't double-count.
     Falls back to embarque.id when the shipper hasn't filled the
     invoice number yet. We collect lots across every proforma on the
     embarque (gfiLot for accepted ones, otherwise no lot). */
  (portalEmbarques || []).forEach(function (e) {
    var proformas = e.proformas || [];
    var hit = false;
    var lotsToAppend = [];
    proformas.forEach(function (p) {
      var lines = p.lines || [];
      if (lines.some(lineRefsCert)) {
        hit = true;
        if (p.gfiLot) lotsToAppend.push(p.gfiLot);
      }
    });
    if (!hit) return;
    var key = e.shipperInvoice ? ("inv:" + e.shipperInvoice) : ("emb:" + e.id);
    upsert(key, {
      shipmentId: e.shipmentId || e.id,
      shipperInvoice: e.shipperInvoice || "",
      date: e.dateArrival || e.dateDeparture || e.createdAt || ""
    }, lotsToAppend[0]);
    /* Surface any additional lots from sibling proformas on the same
       embarque so the usage row shows the full list. */
    if (lotsToAppend.length > 1) {
      var entry = out[keyToIdx[key]];
      for (var i = 1; i < lotsToAppend.length; i++) appendLot(entry, lotsToAppend[i]);
    }
  });
  /* Also union cert.usedInvoices entries (historical / imported records
     from the Excel "Assigned Invoices" sheet that aren't yet tied to a
     shipment). Same dedup key — a shipperInvoice that already appeared
     via a shipment line merges its lot into the existing entry rather
     than creating a second slot. */
  (cert.usedInvoices || []).forEach(function (u) {
    var key = u.shipperInvoice ? ("inv:" + u.shipperInvoice) : ("hist:" + (u.shipmentId || u.date || JSON.stringify(u)));
    upsert(key, {
      shipmentId: u.shipmentId || "",
      shipperInvoice: u.shipperInvoice || "",
      date: u.date || ""
    }, u.lot);
  });
  return out;
}

/* Available count = totalInvoices - unique-shipper-invoice usage. Never
   negative. `bills` and `portalEmbarques` are optional — when omitted,
   only shipment usage is counted (legacy behavior). */
export function availableForCert(cert, shipments, bills, portalEmbarques) {
  var total = parseInt(cert && cert.totalInvoices, 10) || 0;
  var used = computeCertUsage(cert, shipments, bills, portalEmbarques).length;
  return Math.max(0, total - used);
}

/* For the cert-picker dropdown in ShipmentRegistry's line editor: a
   cert is selectable for THIS shipment if either:
     - It still has Available > 0 (a brand-new pick), OR
     - The current shipment's shipper invoice already appears in the
       cert's usage list (so the cert "looks" full from outside, but
       this shipment is one of the ones using it — must stay
       selectable when editing).
   When the current shipment has no shipperInvoice yet, we fall back to
   shipmentId-based identity match. */
export function isCertAvailableForShipment(cert, shipments, currentShipperInvoice, currentShipmentId, bills, portalEmbarques) {
  var usage = computeCertUsage(cert, shipments, bills, portalEmbarques);
  var total = parseInt(cert && cert.totalInvoices, 10) || 0;
  var used = usage.length;
  if (total - used > 0) return true;
  return usage.some(function (u) {
    if (currentShipperInvoice && u.shipperInvoice === currentShipperInvoice) return true;
    if (!currentShipperInvoice && currentShipmentId && String(u.shipmentId) === String(currentShipmentId)) return true;
    return false;
  });
}
